/**
 * Pure-function discoverability policy for usernames.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/UsernameDiscoverabilityPolicy.swift`
 * exactly. All decisions are pure functions of the settings record so the
 * self-test can exhaustively cover every (state × decision) pair without
 * touching disk or shared singletons.
 *
 * ## Background
 *
 * Signal lets a user keep a username but opt out of the server's
 * fuzzy-prefix search index. With `'directLookupOnly'` the server still
 * answers exact-handle lookups (you need the full `name#NNNN`) but
 * excludes the username from any prefix/typo search — so somebody
 * scraping common prefixes cannot enumerate that user.
 *
 * We default new claims to `'directLookupOnly'` because exact-handle lookup
 * is enough for the chat-start flow and fuzzy search is the side-channel
 * that most users won't expect.
 *
 * Pure logic only. No SolidJS imports. No network calls.
 */

import type { Username } from "./Username";

// ─── types ────────────────────────────────────────────────────────────────────

/**
 * Whether the user has opted in to fuzzy-prefix discoverability.
 *
 * Web port of `UsernameDiscoverability` in UsernameDiscoverabilityPolicy.swift.
 */
export type DiscoverabilityMode = "discoverable" | "directLookupOnly";

/**
 * Persisted settings record for one user's discoverability preference.
 *
 * Web port of `UsernameDiscoverabilitySettings`.
 */
export interface UsernameDiscoverabilitySettings {
  readonly username: Username;
  readonly mode: DiscoverabilityMode;
  /** Unix timestamp (seconds) of the last change. */
  readonly updatedAt: number;
}

// ─── policy functions ─────────────────────────────────────────────────────────

/**
 * Whether the server's fuzzy-prefix search index should surface this username.
 *
 * - `null` (no settings yet) → `false`
 * - `'directLookupOnly'`     → `false`
 * - `'discoverable'`         → `true`
 *
 * Web port of `UsernameDiscoverabilityPolicy.canAppearInFuzzySearch(settings:)`.
 */
export function canAppearInFuzzySearch(
  settings: UsernameDiscoverabilitySettings | null | undefined,
): boolean {
  return settings?.mode === "discoverable";
}

/**
 * Whether the user can be found by typing the full `name#NNNN` handle.
 *
 * Always `true` once the user has a claim — exact handles are fundamental
 * to the chat-start flow. Callers must short-circuit for users who have
 * never claimed a username.
 *
 * Web port of `UsernameDiscoverabilityPolicy.canBeFoundByExactHandle(settings:)`.
 */
export function canBeFoundByExactHandle(
  _settings: UsernameDiscoverabilitySettings | null | undefined,
): boolean {
  return true;
}

/**
 * Privacy-preserving default for a new claim: `'directLookupOnly'`.
 * Users opt *in* to fuzzy discoverability rather than opt out.
 *
 * Web port of `UsernameDiscoverabilityPolicy.defaultFor(newUser:at:)`.
 */
export function defaultDiscoverabilitySettings(
  username: Username,
  updatedAt: number,
): UsernameDiscoverabilitySettings {
  return {
    username,
    mode: "directLookupOnly",
    updatedAt,
  };
}
